import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { toast } from 'sonner';

export interface Geofence {
  id: string;
  company_id: string;
  name: string;
  type: 'circle' | 'polygon';
  coordinates: any; // For circle: {lat, lng, radius}, for polygon: [{lat, lng}, ...]
  color: string;
  active: boolean;
  alert_on_entry: boolean;
  alert_on_exit: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
  device_count?: number;
  last_entry?: string;
}

export const useGeofences = (companyId?: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['geofences', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/fleet-gps-geofences?company_id=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch geofences');
      
      const { geofences } = await response.json();
      return geofences as Geofence[];
    },
    enabled: !!companyId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const createMutation = useMutation({
    mutationFn: async (geofence: Omit<Geofence, 'id' | 'created_at' | 'updated_at'>) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/fleet-gps-geofences`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...geofence, company_id: companyId }),
      });

      if (!response.ok) throw new Error('Failed to create geofence');
      
      const { geofence: newGeofence } = await response.json();
      return newGeofence;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences', companyId] });
      toast.success('Geocerca criada com sucesso!');
    },
    onError: (error) => {
      console.error('Error creating geofence:', error);
      toast.error('Erro ao criar geocerca');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Geofence> & { id: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/fleet-gps-geofences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, ...updates }),
      });

      if (!response.ok) throw new Error('Failed to update geofence');
      
      const { geofence } = await response.json();
      return geofence;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences', companyId] });
      toast.success('Geocerca atualizada com sucesso!');
    },
    onError: (error) => {
      console.error('Error updating geofence:', error);
      toast.error('Erro ao atualizar geocerca');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/fleet-gps-geofences`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      });

      if (!response.ok) throw new Error('Failed to delete geofence');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences', companyId] });
      toast.success('Geocerca removida com sucesso!');
    },
    onError: (error) => {
      console.error('Error deleting geofence:', error);
      toast.error('Erro ao remover geocerca');
    },
  });

  return {
    geofences: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    createGeofence: createMutation.mutate,
    updateGeofence: updateMutation.mutate,
    deleteGeofence: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};